import type { CSSProperties } from 'react';
import { COLOR_META } from '@shared/game/board';
import type { GameLogKind, LegalMove, SeatColor } from '@shared/game/types';

export function formatClock(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export function colorLabel(color: SeatColor): string {
  return COLOR_META[color].label;
}

export function modeLabel(mode: 'classic' | 'adventure'): string {
  return mode === 'adventure' ? 'Aventura' : 'Clásico';
}

export function visibilityLabel(visibility: 'public' | 'private'): string {
  return visibility === 'public' ? 'Pública' : 'Privada';
}

export function logIcon(kind: GameLogKind): string {
  switch (kind) {
    case 'roll':
      return 'dice';
    case 'move':
      return 'flag';
    case 'capture':
      return 'capture';
    case 'effect':
      return 'spark';
    case 'shield':
      return 'shield';
    case 'penalty':
      return 'six';
    case 'skip':
      return 'moon';
    case 'win':
      return 'crown';
    case 'turn':
      return 'clock';
    default:
      return 'star';
  }
}

export const UNIT_ROLES = ['Vector', 'Bastión', 'Órbita', 'Filo'] as const;

export function unitRole(slot: number): string {
  return UNIT_ROLES[slot] ?? `Unidad ${slot + 1}`;
}

export function moveLabel(move: LegalMove, slot: number): string {
  if (move.kind === 'exit') return `${unitRole(slot)} · salir del muelle`;
  if (move.to === 71) return `${unitRole(slot)} · entrar en el Núcleo`;
  const steps = move.to - move.from;
  const extra = move.captures > 0 ? ' · captura' : '';
  return `${unitRole(slot)} · avanzar ${steps} casilla${steps === 1 ? '' : 's'}${extra}`;
}

export function moveDetails(move: LegalMove): string {
  if (move.kind === 'exit') return 'Sale a la casilla de salida con el 5';
  if (move.to === 71) return 'Tirada exacta para llegar a la meta';
  if (move.to >= 64) return `Entra en el pasillo (${move.to - 63}/7)`;
  if (move.captures > 0) return `Captura ${move.captures} ficha${move.captures === 1 ? '' : 's'} y repite tirada`;
  if (move.effectPreview) return move.effectPreview.title;
  return `Avanza ${move.to - move.from} casilla${move.to - move.from === 1 ? '' : 's'}`;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      textarea.remove();
      return success;
    } catch {
      return false;
    }
  }
}

export function playerColorStyle(color: SeatColor): CSSProperties {
  return { ['--seat' as string]: `var(--${color})` } as CSSProperties;
}
